"use client"
import {
  TIMELINE_BRANCHPOINTS,
  TIMELINE_ENDPOINTS,
  TIMELINE_FORK_PLANS,
  type PlanATimelineDiagramProps,
  type TimelineBranchpointEntry,
  type TimelineEndpointEntry,
  type TimelinePlanEntry,
} from './planATimelineDiagramModel'
import {
  type BranchpointPlacement,
  type ForkPlanPlacement,
  type PerspectivePlacement,
  type TimelinePlacement,
} from './timelineDiagramLayout'
import { VB_H, VB_W } from './timelineDiagramGeometry'
import { useTimelineDiagramNavigation } from './useTimelineDiagramNavigation'
import { TimelineLines } from './parts/TimelineLines'
import { PlanLabels } from './parts/PlanLabels'
import { BranchpointLabels } from './parts/BranchpointLabels'
import { EndpointLabels } from './parts/EndpointLabels'

// ── Placement constants ─────────────────────────────────────────
// Laid out in the fixed VB_W × VB_H space (see timelineDiagramGeometry).

// y of the horizontal Plan A line — most things placed off it.
const SPINE_Y = 130

const TRUNK_X = 0 // x of the vertical trunk
const FORK_ROW_GAP = 64 // gap between fork rows (and label row)
const FORK_CURVE = 38 // horizontal reach of each elbow before flat
const FORK_ARM_END = TRUNK_X + 175 // x where each arrowhead lands
// Shared center so all plan labels line up vertically, mid-arm not at tip.
const PLAN_LABEL_X = (TRUNK_X + FORK_ARM_END) / 2

// Spacing wide enough that lifted branchpoint titles clear each other.
const BRANCH_FIRST_X = 300
const BRANCH_SPACING_X = 175
const BRANCH_RISE_DX = 22 // x offset of rise (= bend width) from node
const BRANCH_RISE_DY = FORK_ROW_GAP - 22 // vertical rise off spine

// Spine is the only line reaching the right edge; POV tracks sit below it.
const SPINE_END_X = VB_W - 4
// Left edge of every right-hand label (Epilogue + POVs).
const ENDPOINT_LABEL_X = 830
const PERSPECTIVE_ROW_GAP = 50 // pitch of POV track stack below spine
// Fork placed back from the label so the bundle stays tight against it,
// not fanning out early: fork = labelX − descent − lead-in.
const PERSPECTIVE_LABEL_LEAD_IN = 14 // short flat run into the label
const PERSPECTIVE_DESCENT_DX = 95 // S-curve run to settle onto track
const PERSPECTIVE_FORK_X = ENDPOINT_LABEL_X - PERSPECTIVE_DESCENT_DX - PERSPECTIVE_LABEL_LEAD_IN

// ── Path builders ───────────────────────────────────────────────

// Outermost arms (S/D) hand off the rounded trunk corner; interior
// arms (B/C) leave the trunk straight.
const forkArmPath = (y: number, outermost: boolean) =>
  `M ${outermost ? TRUNK_X + FORK_CURVE : TRUNK_X} ${y} L ${FORK_ARM_END} ${y}`

// One continuous rounded trunk easing from top (S) arm to bottom (D) arm.
const trunkPath = (top: number, bottom: number) =>
  `M ${TRUNK_X + FORK_CURVE} ${top}` + // start out along the top (S) arm
  ` Q ${TRUNK_X} ${top} ${TRUNK_X} ${top + FORK_CURVE}` + // round down into the trunk
  ` L ${TRUNK_X} ${bottom - FORK_CURVE}` + // straight down the trunk
  ` Q ${TRUNK_X} ${bottom} ${TRUNK_X + FORK_CURVE} ${bottom}` // round out into the bottom (D) arm

// A branchpoint rise: quarter-turn off the spine, then straight up.
const branchpointRisePath = (x: number) => {
  const endX = x + BRANCH_RISE_DX
  const endY = SPINE_Y - BRANCH_RISE_DY
  const cornerY = SPINE_Y - BRANCH_RISE_DX // as tall as wide: tight
  return `M ${x} ${SPINE_Y} Q ${endX} ${SPINE_Y} ${endX} ${cornerY} L ${endX} ${endY}`
}

// Perspective track: S-curve off the spine then a lead-in to its
// label; shared descent width nests them as one bundle.
const perspectiveTrackPath = (y: number) => {
  const riseEndX = PERSPECTIVE_FORK_X + PERSPECTIVE_DESCENT_DX // x where curve settles
  const midX = (PERSPECTIVE_FORK_X + riseEndX) / 2
  return (
    `M ${PERSPECTIVE_FORK_X} ${SPINE_Y}` +
    ` C ${midX} ${SPINE_Y} ${midX} ${y} ${riseEndX} ${y}` +
    ` L ${riseEndX + PERSPECTIVE_LABEL_LEAD_IN} ${y}`
  )
}

// ── Per-element placement ───────────────────────────────────────
// "above" plans climb from the trunk (S), "below" drop (B/C/D),
// each one FORK_ROW_GAP further from the spine per step.
const abovePlans = TIMELINE_FORK_PLANS.filter(p => p.side === 'above')
const belowPlans = TIMELINE_FORK_PLANS.filter(p => p.side === 'below')

const forkPlan = (entry: TimelinePlanEntry, y: number, outermost: boolean): ForkPlanPlacement => ({
  planId: entry.planId,
  label: entry.label,
  arm: forkArmPath(y, outermost),
  labelAt: { x: PLAN_LABEL_X, y },
})

const forkPlans: ForkPlanPlacement[] = [
  ...abovePlans.map((entry, i) =>
    forkPlan(entry, SPINE_Y - FORK_ROW_GAP * (i + 1), i + 1 === abovePlans.length)),
  ...belowPlans.map((entry, i) =>
    forkPlan(entry, SPINE_Y + FORK_ROW_GAP * (i + 1), i + 1 === belowPlans.length)),
]

// Label lifted one fork row up (the "Plan S" row) so titles align.
const branchpoint = (entry: TimelineBranchpointEntry, i: number): BranchpointPlacement => {
  const x = BRANCH_FIRST_X + BRANCH_SPACING_X * i
  return {
    entry,
    node: { x, y: SPINE_Y },
    rise: branchpointRisePath(x),
    labelAt: { x: x + BRANCH_RISE_DX, y: SPINE_Y - FORK_ROW_GAP },
  }
}

const branchpoints = TIMELINE_BRANCHPOINTS.map(branchpoint)

// Alternate POVs stack below the spine's right end, ROW_GAP apart.
const perspectivePlans = TIMELINE_ENDPOINTS.filter(e => e.kind === 'perspective')

const perspective = (entry: TimelineEndpointEntry, i: number): PerspectivePlacement => {
  const y = SPINE_Y + PERSPECTIVE_ROW_GAP * (i + 1)
  return { entry, track: perspectiveTrackPath(y), labelAt: { x: ENDPOINT_LABEL_X, y } }
}

const perspectives = perspectivePlans.map(perspective)

// ── Placement ───────────────────────────────────────────────────
// All line + label positions in one object; components below are
// pure presentation reading from it.
const PLACEMENT: TimelinePlacement = {
  trunk: trunkPath(
    SPINE_Y - FORK_ROW_GAP * abovePlans.length,
    SPINE_Y + FORK_ROW_GAP * belowPlans.length,
  ),
  forkPlans,
  spine: {
    line: { x1: TRUNK_X, y1: SPINE_Y, x2: SPINE_END_X, y2: SPINE_Y },
    planLabelAt: { x: PLAN_LABEL_X, y: SPINE_Y },
  },
  branchpoints,
  perspectives,
  perspectiveForkNode: perspectives.length > 0 ? { x: PERSPECTIVE_FORK_X, y: SPINE_Y } : null,
  epilogueLabelAt: { x: ENDPOINT_LABEL_X, y: SPINE_Y },
}

// Drawn ink (TimelineLines) with 3 clickable label layers floated over
// it in the same coordinate space, all reading from PLACEMENT above.
export const PlanATimelineDiagram = (props: PlanATimelineDiagramProps) => {
  const { navigate, handleEndpoint, selected, selectedEnding, currentPlan } =
    useTimelineDiagramNavigation(props)

  return (
    <div className="relative w-full" style={{ aspectRatio: `${VB_W} / ${VB_H}` }}>
      <TimelineLines placement={PLACEMENT} />
      <PlanLabels
        placement={PLACEMENT}
        currentPlan={currentPlan}
        selected={selected}
        selectedEnding={selectedEnding}
        onNavigate={navigate}
      />
      <BranchpointLabels placement={PLACEMENT} onNavigate={navigate} />
      <EndpointLabels
        placement={PLACEMENT}
        selected={selected}
        selectedEnding={selectedEnding}
        onSelect={handleEndpoint}
      />
    </div>
  )
}
